"use client";

import React, { useEffect, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Database, Network, Cpu } from "lucide-react";
const stats = [{
  value: 98,
  suffix: "%",
  label: "Success Rate"
}, {
  value: 240,
  suffix: "M+",
  label: "Capital Recovered"
}, {
  value: 15,
  suffix: "k+",
  label: "Forensic Cases"
}, {
  value: 24,
  suffix: "/7",
  label: "Live Monitoring"
}];
function ThreeAnimation() {
  return <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-primary)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-primary)_1px,transparent_1px)] bg-[size:80px_80px] opacity-[0.05]" style={{
      transform: 'perspective(1000px) rotateX(60deg) translateY(-100px)'
    }} />
      
      {}
      <div className="absolute inset-0">
        {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="absolute w-1 h-1 bg-primary/40 rounded-full animate-pulse" style={{
        top: `${(i * 15 + 10) % 100}%`,
        left: `${(i * 25 + 20) % 100}%`,
        animationDelay: `${i * 0.8}s`,
        boxShadow: '0 0 10px var(--color-primary)'
      }} />)}
      </div>
    </div>;
}
function BlockchainAnimation() {
  return <div className="flex flex-col items-center justify-center mb-20 relative">
      <div className="flex items-center gap-6 md:gap-12">
        <div className="relative group">
          <div className="w-14 h-14 md:w-16 md:h-16 border border-primary/30 rounded-sm flex items-center justify-center bg-background/40 backdrop-blur-xl shadow-[0_0_30px_rgba(0,255,255,0.1)] transition-all duration-500 group-hover:border-primary/60">
            <Database className="w-6 h-6 md:w-8 md:h-8 text-primary" />
          </div>
          <div className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full animate-ping" />
        </div>
        
        <div className="flex items-center">
          <div className="w-6 md:w-16 h-[1px] bg-gradient-to-r from-primary/50 to-transparent" />
          <Network className="w-5 h-5 md:w-6 md:h-6 text-primary/40 animate-pulse" />
          <div className="w-6 md:w-16 h-[1px] bg-gradient-to-l from-primary/50 to-transparent" />
        </div>

        <div className="w-14 h-14 md:w-16 md:h-16 border border-primary/30 rounded-sm flex items-center justify-center bg-background/40 backdrop-blur-xl shadow-[0_0_30px_rgba(0,255,255,0.1)] transition-all duration-500 group-hover:border-primary/60">
          <Cpu className="w-6 h-6 md:w-8 md:h-8 text-primary" />
        </div>
      </div>
      <div className="mt-6 flex items-center gap-2">
        <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
        <span className="text-[10px] font-mono text-primary/60 tracking-[0.2em] md:tracking-widest uppercase text-center">
          Neural Network Analysis Active
        </span>
      </div>
    </div>;
}
function AnimatedNumber({
  value,
  suffix
}: {
  value: number;
  suffix: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const duration = 2000;
    const fps = 60;
    const increment = value / (duration / (1000 / fps));
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 1000 / fps);
    return () => clearInterval(timer);
  }, [value, isVisible]);
  return <div data-section-id="76402" ref={ref} className="text-5xl md:text-6xl lg:text-7xl font-mono font-bold text-primary mb-4 tracking-tighter tabular-nums drop-shadow-[0_0_15px_hsla(180,100%,50%,0.3)]">
      {count}
      {suffix}
    </div>;
}
export default function IntelligenceInMotion() {
  return <section id="forensic-performance" className="dark bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      <ThreeAnimation />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--color-primary)_0%,transparent_100%)] opacity-[0.05] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <BlockchainAnimation />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-16 sm:gap-y-12 lg:gap-y-0">
          {stats.map((stat, i) => <div key={i} data-index={i} className="relative flex flex-col items-center text-center px-6 lg:border-r border-primary/10 last:border-0 sm:even:border-r-0 lg:even:border-r">
              <AnimatedNumber value={stat.value} suffix={stat.suffix} />
              <p className="text-muted-foreground text-xs md:text-sm uppercase tracking-widest font-semibold">
                {stat.label}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}